import { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { CircularProgress, Grid, Stack } from '@mui/material';
import { useFormik } from 'formik';
import { required } from 'utils/errorsTextHelper';
import { permissionsUtil } from 'utils/permissionsUtil';
import * as yup from 'yup';

import AcCard from 'components/AcCard/AcCard';
import AcInput from 'components/AcInput/AcInput';
import ActionButton from 'components/ActionButton/ActionButton';

import 'style/forms.scss';
import { ELocalStorageKeys } from 'constants/enums';
import { localStorageUtil } from 'utils/localStorageUtil';
import { User } from 'common/contracts';

const PortalSettings = () => {
  const { portalId } = useParams();

  const userDetails = localStorageUtil.getAny<User>(
    ELocalStorageKeys.USER_DETAILS
  );

  const data: any = userDetails?.portals?.find(
    (portal) => portal.portalId === portalId
  );

  let portalSettingsSchema = yup.object().shape({
    portalName: yup.string().required(required('Name')),
    domain: yup
      .string()
      .required(required('Domain'))
      .matches(
        /^(?!https?:\/\/)(?!.*\/$)[a-zA-Z0-9.-]+$/,
        "Please enter a valid domain name, without http://' or 'https://' prefixes or suffixes"
      )
  });

  const initialValues = useMemo(
    () => ({
      portalName: data?.portalName || '',
      domain: data?.portalDomain || '',
      portalId: data?.portalId || ''
    }),
    [data]
  );

  const {
    values,
    errors,
    touched,
    handleChange,
    setFieldValue,
    handleBlur,
    submitForm,
    isSubmitting,
    dirty,
    isValid
  } = useFormik({
    initialValues,
    validationSchema: portalSettingsSchema,
    enableReinitialize: true,
    onSubmit: async (values, { resetForm }) => {}
  });

  if (!data) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 340px)',
          width: '100%'
        }}
      >
        <CircularProgress title="Loading..." />
      </div>
    );
  }

  return (
    <Grid container className="formContent">
      <Grid item xs={5} className="formContent-minWIdth">
        <Stack>
          <AcCard stackContainer={false} title="General">
            <Grid container rowSpacing={2} columnSpacing={1.5}>
              <Grid item xs={6}>
                <AcInput
                  header="Portal Name"
                  name="portalName"
                  value={values.portalName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.portalName && Boolean(errors.portalName)}
                  helperText={
                    touched.portalName ? errors.portalName?.toString() : ''
                  }
                  disabled
                />
              </Grid>
              <Grid item xs={6}>
                <AcInput
                  header="Portal ID"
                  disabled
                  name="portalId"
                  value={data?.portalId}
                />
              </Grid>
              <Grid item xs={6}>
                <AcInput
                  header="Domain"
                  name="domain"
                  tooltip="Enter a domain name (e.g., example.com ) without 'http://' or 'https://'"
                  value={values.domain}
                  disabled
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.domain && Boolean(errors.domain)}
                  helperText={touched.domain ? errors.domain?.toString() : ''}
                  enableNewTabOpen
                />
              </Grid>
            </Grid>
          </AcCard>
        </Stack>
        <Grid item xs={6} mt={3}>
          <ActionButton
            variant="outlined"
            text="Update"
            onClick={submitForm}
            disabled={!isValid || !dirty || isSubmitting}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export default PortalSettings;
